import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CANNED_FIELD_METADATA,
  CHECKBOX_CHECKED,
  createAlineCanvas,
  defaultSignatureFieldHeightPx,
  defaultSignatureFieldWidthPx,
  minSignatureFieldHeightPx,
  minSignatureFieldWidthPx,
  PDF_CHECKBOX_UNCHECKED,
  PDFCustomDataKey,
  Queries,
  Select,
  signatureFieldAspectRatio,
  walkThroughParamKey,
} from '@aline/common';
import { link, params } from '@aline/routes';
import { makeVar } from '@apollo/client';
import { useApolloClient, useReactiveVar } from '@apollo/client/react';
import { alpha } from '@mui/material';
import type { Core } from '@pdftron/webviewer';
import { kebabCase } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useAsync, usePreviousDistinct } from 'react-use';
import { IconColors } from 'src/components/icons/icon-utils';
import {
  getVariablePanelFocusedField,
  setVariablePanelFocusedField,
  useVariablePanelFocusedField,
} from 'src/components/variables';
import type { FocusedField } from 'src/components/variables';
import type { ViewerDocInfo } from 'src/components/viewer/FileViewerBody';
import { getViewerDocInfo } from 'src/components/viewer/FileViewerBody';
import {
  flattenExternalSignedSignatureAppearances,
  hideFlattenedSignatureWidgetElements,
} from 'src/components/viewer/flatten-external-signature-appearances';
import { setSignatureStampImageData } from 'src/components/viewer/helpers/signature-stamp-image-data';
import {
  pdfTronCore,
  pdfTronWorkerPath,
  useMaybeDocumentViewer,
} from 'src/components/viewer/viewer-context';
import {
  useEditorTabStateActionsForTabId,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import { useStable } from 'src/editor/helpers/useStable';
import { mobileSheetAutoFocusVar } from 'src/editor/mobile/signing/mobile-sheet-autofocus';
import { getFieldDisabledReason, useGetUserSignatureImg } from 'src/editor/popovers';
import { getPathWithDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { Apollo } from 'src/gql/generated';
import { blobToBase64, useDocumentParams, useIsAtVersionHistoryRoute } from 'src/helpers';
import { IS_MOBILE } from 'src/helpers/environment';
import {
  createDocFileUrlRefresher,
  fetchFreshFilestackBlob,
} from 'src/helpers/integrations/filestack-download';
import {
  getEditorUserStateForTabId,
  useGetCurrentDoc,
  useIsAuthorizedToEditStorable,
  useOverrideGlobalCursor,
} from 'src/hooks';
import { signatureDialogOpenVar } from 'src/layout-engine';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import { pushNotification } from 'src/notifications';
import { alineDefaultFontFamily, appColors } from 'src/theme';
import { v4 as uuid4 } from 'uuid';

import { createAlineWidgetInnerElement } from './create-inner-element-args';
import { getIsAlineFieldName, getPDFPageCoords } from './helpers';
import type { PDFPageCoordsInfo } from './helpers';
import { getPDFCheckboxSelectedAnswerIds, PDFFieldType, useSyncAnnotations } from './hooks';
import { isPdfWidgetFocused } from './pdf-widget-focus';
import { syncPageWidgetElements } from './sync-page-widget-elements';

export const pdfCheckboxWidgetBorderColor = '#000000';

export const CHECKBOX_LABEL_GAP_PX = 4;
export const CHECKBOX_LABEL_MAX_WIDTH_PX = 240;

const CHECKBOX_DRAG_CLICK_THRESHOLD_PX = 3;
const CHECKBOX_CLICK_SUPPRESS_MS = 300;

export const defaultPDFFieldAttributes = {
  borderSizePx: 1,
  focusedBorderSizePx: 2,
  fontSizePx: 18,
  nonSigFieldHeightPx: 29,
  nonSigFieldMinFontPx: 8,
  nonSigFieldMinWidthPx: 56,
  nonSigFieldWidthPx: 150,
  paddingLeftPx: 8,
  paddingRightPx: 8,
  sigFieldAspectRatio: signatureFieldAspectRatio,
  sigFieldHeightPx: defaultSignatureFieldHeightPx,
  sigFieldMinHeightPx: minSignatureFieldHeightPx,
  sigFieldMinWidthPx: minSignatureFieldWidthPx,
  sigFieldWidthPx: defaultSignatureFieldWidthPx,
} as const;

export const pdfCheckboxRowTopOffsetPx =
  (defaultPDFFieldAttributes.nonSigFieldHeightPx - defaultPDFFieldAttributes.fontSizePx) / 2;

export enum ZoomConvertDir {
  To,
  From,
}

interface DraggedEl {
  annotation: Core.Annotations.WidgetAnnotation;
  el: HTMLElement;
}

interface GroupDragOverlayTarget {
  parentEl: HTMLElement;
  startLeft: number;
  startTop: number;
}

interface GroupMemberStartPosition {
  annotation: Core.Annotations.Annotation;
  startX: number;
  startY: number;
}

const checkboxPdfFieldCallbacksByViewer = new WeakMap<
  Core.DocumentViewer,
  {
    getDisabledReason: (fieldName: string) => Promise<string>;
    revertUnauthorizedChange: (
      field: Core.Annotations.Forms.Field,
      disabledReason: string
    ) => Promise<void>;
    sync: (field: Core.Annotations.Forms.Field) => Promise<void>;
  }
>();

interface DragState {
  checkboxFieldValueBeforeDrag: string | null;
  draggedEl: DraggedEl | null;
  groupDragTargets: GroupDragOverlayTarget[] | null;
  groupMemberStartPositions: GroupMemberStartPosition[] | null;
  mouseDownX: number;
  mouseDownY: number;
  offsetLeft: number;
  offsetTop: number;
  pageMouseDownX: number;
  pageMouseDownY: number;
}

const defaultDragState: DragState = {
  checkboxFieldValueBeforeDrag: null,
  draggedEl: null,
  groupDragTargets: null,
  groupMemberStartPositions: null,
  mouseDownX: 0,
  mouseDownY: 0,
  offsetLeft: 0,
  offsetTop: 0,
  pageMouseDownX: 0,
  pageMouseDownY: 0,
};

const dragStateVar = makeVar<DragState>(defaultDragState);

function getAnnotationInnerHTMLElement(
  annotation: Core.Annotations.Annotation
): HTMLElement | null {
  if (!('innerElement' in annotation)) return null;
  const inner = annotation.innerElement;
  return inner instanceof HTMLElement ? inner : null;
}

function buildGroupDragOverlayTargets(
  group: Core.Annotations.Annotation[],
  documentViewer: Core.DocumentViewer,
  tabId: UUID | null
): GroupDragOverlayTarget[] | null {
  const primary = getCheckboxGroupPrimary(group);
  const annotationManager = documentViewer.getAnnotationManager();

  const targets = group
    .map(annotation => {
      if (primary && isCheckboxOptionLabel(annotation)) {
        const hitEl =
          document.getElementById(checkboxLabelHitId(annotation.Id)) ??
          wireCheckboxGroupHitOverlay({
            annotationManager,
            documentViewer,
            labelAnnotation: annotation,
            primary,
            tabId,
          });
        if (!hitEl) return null;
        return { parentEl: hitEl, startLeft: hitEl.offsetLeft, startTop: hitEl.offsetTop };
      }

      const inner = getAnnotationInnerHTMLElement(annotation);
      if (!inner?.parentElement) return null;
      return {
        parentEl: inner.parentElement,
        startLeft: inner.parentElement.offsetLeft,
        startTop: inner.parentElement.offsetTop,
      };
    })
    .filter((target): target is GroupDragOverlayTarget => target !== null);
  return targets.length > 0 ? targets : null;
}

function setGroupDragState(args: {
  documentViewer: Core.DocumentViewer;
  dragEl: HTMLElement;
  draggedAnnotation: Core.Annotations.WidgetAnnotation;
  event: MouseEvent;
  group: Core.Annotations.Annotation[];
  tabId: UUID | null;
  offsetLeft?: number;
  offsetTop?: number;
}): void {
  const { documentViewer, dragEl, draggedAnnotation, event, group, offsetLeft, offsetTop, tabId } =
    args;
  const coords = getPDFPageCoords(documentViewer, { event });
  if (!coords || !dragEl.parentElement) return;

  const isCheckbox =
    draggedAnnotation instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation;
  setDragState({
    checkboxFieldValueBeforeDrag: isCheckbox
      ? String(draggedAnnotation.getField()?.getValue() ?? '')
      : null,
    draggedEl: { annotation: draggedAnnotation, el: dragEl },
    groupDragTargets: buildGroupDragOverlayTargets(group, documentViewer, tabId),
    groupMemberStartPositions: group.map(annotation => ({
      annotation,
      startX: annotation.X,
      startY: annotation.Y,
    })),
    mouseDownX: coords.windowCoords.x,
    mouseDownY: coords.windowCoords.y,
    offsetLeft: offsetLeft ?? dragEl.offsetLeft,
    offsetTop: offsetTop ?? dragEl.offsetTop,
    pageMouseDownX: coords.pageCoords.x,
    pageMouseDownY: coords.pageCoords.y,
  });
}

// TODO: Refactor to use useOverrideGlobalCursor
function setCheckboxGroupCursor(state: DragState, cursor: 'grabbing' | null): void {
  document.body.style.cursor = cursor === 'grabbing' ? 'grabbing' : '';
  if (!isCheckboxGroupDrag(state)) {
    if (cursor === null && state.draggedEl?.el) state.draggedEl.el.style.cursor = '';
    return;
  }
  const next = cursor ?? 'pointer';
  for (const target of state.groupDragTargets ?? []) target.parentEl.style.cursor = next;
  if (state.draggedEl?.el) state.draggedEl.el.style.cursor = next;
}

function wireCheckboxGroupHitOverlay(args: {
  annotationManager: Core.AnnotationManager;
  documentViewer: Core.DocumentViewer;
  labelAnnotation: Core.Annotations.FreeTextAnnotation;
  primary: Core.Annotations.CheckButtonWidgetAnnotation;
  tabId: UUID | null;
}): HTMLElement | null {
  const { annotationManager, documentViewer, labelAnnotation, primary, tabId } = args;
  const pageContainer = document.getElementById(`pageWidgetContainer${labelAnnotation.PageNumber}`);
  const primaryParent = primary.innerElement?.parentElement;
  if (!pageContainer || !primaryParent) return null;

  const hitId = checkboxLabelHitId(labelAnnotation.Id);
  let hitEl = document.getElementById(hitId);
  if (!hitEl) {
    const overlay = document.createElement('div');
    overlay.id = hitId;
    pageContainer.appendChild(overlay);
    overlay.addEventListener('click', e => e.stopPropagation());
    overlay.addEventListener('mousedown', event => {
      if (!canInteractWithPdfCheckbox(tabId) || dragStateVar().draggedEl) return;
      setGroupDragState({
        documentViewer,
        dragEl: overlay,
        draggedAnnotation: primary,
        event,
        group: annotationManager.getGroupAnnotations(labelAnnotation),
        tabId,
      });
    });
    hitEl = overlay;
  }

  const zoom = documentViewer.getZoomLevel();
  const rowTopPx = primaryParent.offsetTop - pdfCheckboxRowTopOffsetPx * zoom;
  const checkboxWidthPx = defaultPDFFieldAttributes.fontSizePx;
  const labelLeftPx = primaryParent.offsetLeft + (checkboxWidthPx + CHECKBOX_LABEL_GAP_PX) * zoom;
  const labelWidthPx = getCheckboxOptionLabelWidthPts(labelAnnotation) * zoom;

  Object.assign(
    hitEl.style,
    kebabCaseCSS({
      background: 'transparent',
      cursor: labelWidthPx > 0 ? 'pointer' : 'default',
      height: `${defaultPDFFieldAttributes.nonSigFieldHeightPx * zoom}px`,
      left: `${labelLeftPx}px`,
      pointerEvents: labelWidthPx > 0 ? 'auto' : 'none',
      position: 'absolute',
      top: `${rowTopPx}px`,
      width: `${labelWidthPx}px`,
      // less than the widget container so label overlays never steal checkbox clicks when regions overlap.
      zIndex: 49,
    })
  );

  return hitEl;
}

let didDragMoveField = false;

// Movement (in CSS px) a field must travel before a drag suppresses the trailing click-to-focus.
const FIELD_DRAG_MOVE_THRESHOLD_PX = 3;

// Default PDFTron's widget z-index is 35 for both fields and links.
const FIELD_WIDGET_Z_INDEX = 36;

function setDragState(stateOrFunc: ((state: DragState) => DragState) | Partial<DragState>): void {
  const existingState = dragStateVar();
  const newState: DragState =
    typeof stateOrFunc === 'function'
      ? stateOrFunc(existingState)
      : { ...existingState, ...stateOrFunc };

  dragStateVar(newState);
}

export const signatureB64Var = makeVar<SignatureB64State>({
  initials: null,
  signature: null,
});

function setSignatureB64(
  stateOrFunc: ((state: SignatureB64State) => SignatureB64State) | Partial<SignatureB64State>
): void {
  const existingState = signatureB64Var();
  const newState: SignatureB64State =
    typeof stateOrFunc === 'function'
      ? stateOrFunc(existingState)
      : { ...existingState, ...stateOrFunc };

  signatureB64Var(newState);
}

const SIGNATURE_FIELD_DATA_ATTR = 'data-signature-field';

interface UseInstantiateFileViewer {
  disposeFileViewer: () => void;
  documentViewer: Core.DocumentViewer | null;
}

/**
 * Instantiates a PDFTron DocumentViewer object.
 */
export function useInstantiateFileViewer(): UseInstantiateFileViewer {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { document: currentDocument } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);
  const currentDocumentRef = useRef(currentDocument);
  currentDocumentRef.current = currentDocument;
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  const tabId = useCurrentTabIdOrThrow();
  const { openDrawer } = useGlobalDrawer();
  const { syncAnnotations } = useSyncAnnotations(tabId);
  const documentViewer = useStable(() => {
    // Path to PDFTron Web Worker *relative to dist*.
    pdfTronCore.setWorkerPath(pdfTronWorkerPath);
    const documentViewer = new pdfTronCore.DocumentViewer();

    /**
     * WebViewer assumes its scroll/viewer elements always exist: its internal getters fall back to
     * document.getElementById('DocumentViewer'/'viewer') — elements that exist in its demo HTML
     * but not in ours — and its requestAnimationFrame read loop dereferences them without null
     * checks (null scrollLeft crash). Default both to detached elements so every read is safe;
     * FileViewerBody re-points them to the real DOM nodes when it mounts.
     */
    documentViewer.setScrollViewElement(document.createElement('div'));
    documentViewer.setViewerElement(document.createElement('div'));
    documentViewer.enableAnnotations();
    // Don't run embedded JavaScript.  We have no idea what's in it...
    pdfTronCore.disableEmbeddedJavaScript();

    return documentViewer;
  }, []);

  useSetSignatureB64();

  useEffect(() => {
    function updateZoomCSS() {
      const currentZoom = documentViewer.getZoomLevel();
      const pageContainers = document.querySelectorAll<HTMLElement>('[id^="pageWidgetContainer"]');
      pageContainers.forEach(container => {
        container.style.setProperty('--viewer-zoom', String(currentZoom));
      });
    }

    updateZoomCSS();
    documentViewer.addEventListener('zoomUpdated', updateZoomCSS);

    return () => {
      documentViewer.removeEventListener('zoomUpdated', updateZoomCSS);
    };
  }, [documentViewer]);

  useEffect(() => {
    /**
     * WebViewer introduced form-field highlighting by default, rendering a translucent blue
     * overlay in version 11.5.0. disable it
     */
    const fieldManager = documentViewer.getAnnotationManager().getFieldManager();
    if (typeof fieldManager.disableWidgetHighlighting === 'function')
      fieldManager.disableWidgetHighlighting();
  }, [documentViewer]);

  useEffect(() => {
    function focusFieldInOutline(field: FocusedField): void {
      navigate(
        getPathWithDrawerSearchParam(
          link.DocumentDetailFields({ documentId }),
          window.location.search,
          params.rightPanel.editFieldId,
          null
        ),
        { replace: true }
      );
      openDrawer();
      setVariablePanelFocusedField(tabId, field);
    }

    function openFieldEditPanel(field: FocusedField): void {
      navigate(
        getPathWithDrawerSearchParam(
          link.DocumentDetailFields({ documentId }),
          window.location.search,
          params.rightPanel.editFieldId,
          field.id
        ),
        { replace: true }
      );
      openDrawer();
      setVariablePanelFocusedField(tabId, field);
    }

    const showAuthError = () =>
      pushNotification({
        key: uuid4(),
        message: 'User not authenticated.',
        variant: 'error',
      });

    async function resolveVariableForFieldName(fieldName: string) {
      const [{ data: fieldData }, { data: userData }] = await Promise.all([
        client.query<Apollo.GetDocumentFieldsQuery, Apollo.GetDocumentFieldsQueryVariables>({
          fetchPolicy: 'cache-first',
          query: Apollo.GetDocumentFieldsDocument,
          variables: { documentId },
        }),
        client.query<Apollo.CurrentUserQuery, Apollo.CurrentUserQueryVariables>({
          fetchPolicy: 'cache-first',
          query: Apollo.CurrentUserDocument,
        }),
      ]);
      const variableNodeId =
        Queries.parseCheckboxOptionNodeId(fieldName)?.variableNodeId ?? fieldName;
      return {
        currentUserId: userData?.currentUser?.id,
        variable: fieldData?.document?.variables?.find(v => v.nodeId === variableNodeId),
        variableNodeId,
      };
    }

    async function syncCheckboxVariableFromPdfField(
      field: Core.Annotations.Forms.Field
    ): Promise<void> {
      const fieldName = field.name;
      const { currentUserId, variable, variableNodeId } =
        await resolveVariableForFieldName(fieldName);
      if (!currentUserId) {
        showAuthError();
        return;
      }
      if (!variable) return;
      const { answerOptions, maxRequired, selectedAnswers } = variable;

      const selectedAnswerIds = getPDFCheckboxSelectedAnswerIds(
        documentViewer.getAnnotationManager().getFieldManager(),
        variableNodeId,
        answerOptions?.options ?? []
      );

      const isChecking = field.getValue() === CHECKBOX_CHECKED;
      if (maxRequired !== null && isChecking && selectedAnswerIds.length > maxRequired) {
        const parsed = Queries.parseCheckboxOptionNodeId(fieldName);
        const isAuthorizedChecked = (selectedAnswers ?? []).some(
          answer => answer.id === parsed?.optionId
        );
        field.setValue(isAuthorizedChecked ? CHECKBOX_CHECKED : PDF_CHECKBOX_UNCHECKED);
        await lockSquareCheckboxWidgetsAndRedraw(documentViewer.getAnnotationManager(), field);
        notifyCheckboxFieldDisabled(
          `You can select at most ${maxRequired} ${maxRequired === 1 ? 'option' : 'options'}.`
        );
        return;
      }

      const sortIds = (ids: string[]) => ids.slice().sort().join(',');
      if (
        sortIds(selectedAnswerIds) !==
        sortIds((variable.selectedAnswers ?? []).map(answer => answer.id))
      ) {
        await client.mutate({
          mutation: Apollo.UpdateDocumentVariableDocument,
          variables: {
            input: {
              id: variable.id,
              props: {
                selectedAnswerIds,
                ...(selectedAnswerIds.length > 0 ? { skipped: false } : {}),
              },
            },
          },
        });
      }
    }

    async function revertUnauthorizedCheckboxPdfFieldChange(
      field: Core.Annotations.Forms.Field,
      disabledReason: string
    ): Promise<void> {
      const parsed = Queries.parseCheckboxOptionNodeId(field.name);
      if (!parsed) return;

      const { variable } = await resolveVariableForFieldName(field.name);
      if (!variable) return;

      const isAuthorizedChecked = (variable.selectedAnswers ?? []).some(
        answer => answer.id === parsed.optionId
      );
      field.setValue(isAuthorizedChecked ? CHECKBOX_CHECKED : PDF_CHECKBOX_UNCHECKED);

      await lockSquareCheckboxWidgetsAndRedraw(documentViewer.getAnnotationManager(), field);
      notifyCheckboxFieldDisabled(disabledReason);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (pdfTronCore.Annotations.WidgetAnnotation as any).getCustomStyles = (
      widget: Core.Annotations.WidgetAnnotation
    ) => {
      const isAlineAnnotation = getIsAlineFieldName(widget.fieldName);
      if (!isAlineAnnotation) return;

      if (widget instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation) {
        return kebabCaseCSS({
          boxSizing: 'border-box',
          cursor: 'pointer',
          display: 'inherit',
          height: '100%',
          outline: 'none',
          paddingLeft: '0',
          paddingRight: '0',
          width: '100%',
        });
      }

      return kebabCaseCSS({
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'inherit',
        height: '100%',
        outline: 'none',
        paddingLeft: `${defaultPDFFieldAttributes.paddingLeftPx / defaultPDFFieldAttributes.fontSizePx}em`,
        paddingRight: `${defaultPDFFieldAttributes.paddingRightPx / defaultPDFFieldAttributes.fontSizePx}em`,
        width: '100%',
      });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (pdfTronCore.Annotations.WidgetAnnotation as any).getContainerCustomStyles = (
      widget: Core.Annotations.WidgetAnnotation
    ) => {
      const isAlineAnnotation = getIsAlineFieldName(widget.fieldName);
      if (!isAlineAnnotation) return;

      const field = widget.getField();
      const { draggedEl } = dragStateVar();
      const isComplete = getIsPDFFieldComplete(field);
      const focusedField = getVariablePanelFocusedField(tabId);
      const isDraggedAnnot = draggedEl?.annotation === widget;
      const isFocusedField = isPdfWidgetFocused(widget, focusedField);
      const isSignature = widget instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation;
      const isSignedSignature = isSignature && Boolean(widget.getAssociatedSignatureAnnotation());

      if (isSignedSignature) return kebabCaseCSS({ display: 'none' });

      if (isSignature) {
        return kebabCaseCSS({
          backgroundColor: appColors.defaultFieldBackground,
          border:
            isFocusedField || isDraggedAnnot
              ? `${defaultPDFFieldAttributes.focusedBorderSizePx}px dashed ${IconColors.Primary}`
              : `${defaultPDFFieldAttributes.borderSizePx}px dashed ${IconColors.Primary}`,
          borderRadius: '3px',
          boxSizing: 'border-box',
          cursor: 'pointer',
          outline: 'none',
          zIndex: FIELD_WIDGET_Z_INDEX,
        });
      }

      if (widget instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation) {
        return kebabCaseCSS({
          backgroundColor: 'transparent',
          border:
            isFocusedField || isDraggedAnnot
              ? `${defaultPDFFieldAttributes.focusedBorderSizePx}px solid ${pdfCheckboxWidgetBorderColor}`
              : `${defaultPDFFieldAttributes.borderSizePx}px solid ${pdfCheckboxWidgetBorderColor}`,
          borderRadius: '0',
          boxSizing: 'border-box',
          cursor: 'pointer',
          zIndex: 50,
        });
      }

      return kebabCaseCSS({
        backgroundColor:
          !isComplete && !isFocusedField ? alpha(IconColors.Primary, 0.1) : 'transparent',
        borderRadius: '3px',
        boxShadow: `inset 0 0 0 ${
          isFocusedField || isDraggedAnnot
            ? defaultPDFFieldAttributes.focusedBorderSizePx
            : defaultPDFFieldAttributes.borderSizePx
        }px ${IconColors.Primary}`,
        boxSizing: 'border-box',
        cursor: 'pointer',
        zIndex: FIELD_WIDGET_Z_INDEX,
      });
    };

    pdfTronCore.Annotations.setCustomCreateInnerElementHandler(
      pdfTronCore.Annotations
        .TextWidgetAnnotation as unknown as Core.Annotations.TextWidgetAnnotation,
      (annotationManager, options) => {
        const created = createAlineWidgetInnerElement(annotationManager, options);
        if (!created.isAline) return created.el;

        const { annotation, el } = created;
        el.dataset.cy = Select.document.fields.inputFieldEl;
        el.style.setProperty('overflow', 'hidden', 'important');
        stopFieldTapFromTriggeringLink(el);

        const cannedFieldId = annotation.getCustomData(PDFCustomDataKey.cannedFieldId);
        if (cannedFieldId) {
          const workflowId = currentDocumentRef.current?.workflow?.id;
          if (workflowId) {
            // cannedFieldId is the CannedField's nodeId, not its PK — resolve
            // it via the workflow list rather than GetCannedField(id).
            const cached = client.readQuery<
              Apollo.GetWorkflowCannedFieldsQuery,
              Apollo.GetWorkflowCannedFieldsQueryVariables
            >({
              query: Apollo.GetWorkflowCannedFieldsDocument,
              variables: { workflowId },
            });
            const cf = cached?.workflow?.cannedFields.find(f => f.nodeId === cannedFieldId);
            if (cf && el.isConnected) {
              el.title = CANNED_FIELD_METADATA[cf.variant].buildTooltip(
                cf.value ?? cf.defaultValue ?? null
              );
            }
          }

          el.addEventListener('click', e => {
            e.stopPropagation();
            if (didDragMoveField) return;
            if (IS_MOBILE) mobileSheetAutoFocusVar(true);
            setVariablePanelFocusedField(tabId, {
              __typename: 'CannedField',
              id: cannedFieldId,
              nodeId: cannedFieldId,
              // Which placement was clicked — the Outline row is shared by all
              // of them, so without this the panel scrolls back to the first.
              placementNodeId: annotation.getCustomData(PDFCustomDataKey.nodeId),
            });
            openDrawer();
          });

          return attachFieldElDragListeners({ annotation, documentViewer, el, tabId });
        }

        el.addEventListener('click', async e => {
          // Stop propagation so the DocumentViewer.click listeners don't fire when clicking the field.
          e.stopPropagation();
          // The browser fires this click after a drag ends; don't focus/scroll if the field moved.
          if (didDragMoveField) return;

          const { currentUserId, variable } = await resolveVariableForFieldName(
            annotation.fieldName
          );
          if (!currentUserId) return showAuthError();
          if (!variable) return;
          focusFieldInOutline(variable);
          mobileSheetAutoFocusVar(true);
        });

        return attachFieldElDragListeners({
          annotation,
          documentViewer,
          el,
          tabId,
        });
      }
    );

    pdfTronCore.Annotations.setCustomCreateInnerElementHandler(
      pdfTronCore.Annotations
        .SignatureWidgetAnnotation as unknown as Core.Annotations.SignatureWidgetAnnotation,
      (annotationManager, options) => {
        const created = createAlineWidgetInnerElement(annotationManager, options);
        if (!created.isAline) return created.el;

        const { annotation, el } = created;
        const isSignatureAnnot =
          annotation instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation;
        if (!isSignatureAnnot) throw new Error('Expected SignatureWidgetAnnotation.');

        el.dataset.cy = Select.document.fields.inputFieldEl;
        // TODO: See if we can use a native button element OR make this fully accessible
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', 'Signature field');
        stopFieldTapFromTriggeringLink(el);
        el.addEventListener('click', async e => {
          // Stop propagation so the DocumentViewer.click listeners don't fire when clicking the field.
          e.stopPropagation();
          // The browser fires this click after a drag ends; don't focus/scroll if the field moved.
          if (didDragMoveField) return;

          const { data: fieldData } = await client.query<
            Apollo.GetDocumentFieldsQuery,
            Apollo.GetDocumentFieldsQueryVariables
          >({
            fetchPolicy: 'cache-first',
            query: Apollo.GetDocumentFieldsDocument,
            variables: { documentId },
          });
          const { data: userData } = await client.query<
            Apollo.CurrentUserQuery,
            Apollo.CurrentUserQueryVariables
          >({
            fetchPolicy: 'cache-first',
            query: Apollo.CurrentUserDocument,
          });
          const currentUserId = userData?.currentUser?.id;
          const allSignatures = fieldData?.document.signatureFields ?? [];
          const signatureField = allSignatures.find(
            signature => signature.nodeId === annotation.fieldName
          );
          if (!currentUserId) {
            pushNotification({
              key: uuid4(),
              message: 'User not authenticated.',
              variant: 'error',
            });
            return;
          }
          if (!signatureField) return;

          // If not in signing ceremony, click on signature opens the edit panel.
          const userState = getEditorUserStateForTabId(tabId);
          if (!userState?.isInSigningCeremony) {
            openFieldEditPanel(signatureField);
            return;
          }

          // We're in signing ceremony now...
          if (userState?.userDeclinedSigning) {
            pushNotification({
              key: uuid4(),
              message: 'You have declined to sign this document.',
              variant: 'warning',
            });
            setVariablePanelFocusedField(tabId, signatureField);
            return;
          }

          const isAssignedToSigField = signatureField.action?.assignee?.id === currentUserId;
          if (!isAssignedToSigField) {
            pushNotification({
              key: uuid4(),
              message: 'Not assigned to signature field.',
              variant: 'warning',
            });
            setVariablePanelFocusedField(tabId, signatureField);
            return;
          }

          const sigImg = annotation.getAssociatedSignatureAnnotation();
          const isSigned = Boolean(sigImg);
          if (isSigned) return;

          const { signature } = signatureB64Var();
          if (!signature) {
            /**
             * No cached signature. Desktop opens the standalone adoption
             * dialog; mobile routes through the walkthrough sheet, which
             * renders MobileAdoptAndSignBody when signatureImg is null.
             */
            if (IS_MOBILE) {
              setVariablePanelFocusedField(tabId, signatureField);
            } else {
              signatureDialogOpenVar(true);
            }
            return;
          }

          await applyPDFSignatureStamp({
            documentViewer,
            signatureB64: signature.signatureB64,
            signatureImgId: signature.signatureImgId,
            widget: annotation,
          });
          await syncAnnotations(documentViewer);
          /**
           * On mobile the field just got auto-signed silently; focusing it
           * here would pop the mobile sheet on top of an already-signed
           * field. Desktop still focuses so the right-panel reflects state.
           */
          if (!IS_MOBILE) {
            setVariablePanelFocusedField(tabId, signatureField);
          }
        });

        return attachFieldElDragListeners({
          annotation,
          documentViewer,
          el,
          tabId,
        });
      }
    );

    pdfTronCore.Annotations.setCustomCreateInnerElementHandler(
      pdfTronCore.Annotations
        .CheckButtonWidgetAnnotation as unknown as Core.Annotations.CheckButtonWidgetAnnotation,
      (annotationManager, options) => {
        const created = createAlineWidgetInnerElement(annotationManager, options);
        if (!created.isAline) return created.el;

        const { annotation, el } = created;
        el.dataset.cy = Select.document.fields.inputFieldEl;
        el.addEventListener('click', e => e.stopPropagation());

        return attachFieldElDragListeners({
          annotation,
          documentViewer,
          el,
          tabId,
        });
      }
    );

    pdfTronCore.Annotations.setCustomCreateSignHereElementHandler(
      (tool, { originalCreateSignHereElement }) => {
        const userState = getEditorUserStateForTabId(tabId);
        const el = originalCreateSignHereElement(tool);
        const span = document.createElement('span');

        el.innerHTML = '';
        span.textContent = userState?.isInSigningCeremony
          ? 'Click to sign'
          : 'Click to edit signature';
        el.setAttribute(SIGNATURE_FIELD_DATA_ATTR, '');
        el.removeAttribute('style');
        Object.assign(
          el.style,
          kebabCaseCSS({
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            height: `calc(${defaultPDFFieldAttributes.sigFieldHeightPx}px * var(--viewer-zoom, 1))`,
            padding: `calc(${defaultPDFFieldAttributes.paddingLeftPx}px * var(--viewer-zoom, 1))`,
            width: `calc(${defaultPDFFieldAttributes.sigFieldWidthPx}px * var(--viewer-zoom, 1))`,
          })
        );
        Object.assign(
          span.style,
          kebabCaseCSS({
            color: appColors.iconsType,
            fontFamily: alineDefaultFontFamily,
            fontSize: `calc(${defaultPDFFieldAttributes.fontSizePx}px * var(--viewer-zoom, 1))`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            userSelect: 'none',
            whiteSpace: 'nowrap',
          })
        );
        el.insertAdjacentHTML(
          'afterbegin',
          '<svg style="flex-shrink: 0; height: calc(24px * var(--viewer-zoom, 1)); margin-right: calc(8px * var(--viewer-zoom, 1)); width: calc(24px * var(--viewer-zoom, 1));" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" stroke="#324A52" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19.699 4.801c-.52-.517-1.225-.806-1.959-.8-.733.004-1.435.302-1.947.827L4.837 15.783 3.5 21l5.217-1.338L19.672 8.706c.525-.512.823-1.213.828-1.947.005-.733-.284-1.438-.801-1.958z" clip-rule="evenodd"></path><path stroke="#324A52" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.48 5.141l3.878 3.879M4.838 15.782l3.883 3.876"></path></svg>'
        );
        el.appendChild(span);

        return el;
      }
    );
    checkboxPdfFieldCallbacksByViewer.set(documentViewer, {
      getDisabledReason: async fieldName => {
        const userState = getEditorUserStateForTabId(tabId);
        const { currentUserId, variable } = await resolveVariableForFieldName(fieldName);
        const doc = currentDocumentRef.current;
        return getFieldDisabledReason({
          canEdit: canEditRef.current,
          currentUserId,
          document: doc ? { variant: doc.variant, workflow: doc.workflow } : null,
          field: variable ?? null,
          isFieldInDocument: true,
          isReadOnly: userState?.isReadOnly ?? false,
        });
      },
      revertUnauthorizedChange: revertUnauthorizedCheckboxPdfFieldChange,
      sync: syncCheckboxVariableFromPdfField,
    });

    return () => {
      checkboxPdfFieldCallbacksByViewer.delete(documentViewer);
    };
  }, [client, documentId, documentViewer, navigate, openDrawer, syncAnnotations, tabId]);

  /**
   * Disposes the viewer when its owner truly unmounts (not Activity hide). WebViewer runs an
   * internal requestAnimationFrame read/write scheduler that outlives React's DOM removal; async
   * work (e.g. annotation import after signing) can queue a render request that fires after
   * navigation and crashes reading scroll state. dispose() cancels the pending frame and prevents
   * any future scheduling.
   */
  const disposeFileViewer = useCallback(() => documentViewer.dispose(), [documentViewer]);

  return {
    disposeFileViewer,
    documentViewer,
  };
}

function attachFieldElDragListeners(args: {
  annotation: Core.Annotations.WidgetAnnotation;
  documentViewer: Core.DocumentViewer;
  el: HTMLElement;
  tabId: UUID;
}): HTMLElement {
  const { annotation, documentViewer, el, tabId } = args;

  el.addEventListener('mousedown', event => {
    didDragMoveField = false;
    if (!el.parentElement) return;
    const isCheckbox = annotation instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation;
    if (isCheckbox ? !canInteractWithPdfCheckbox(tabId) : !canDragPdfFields(tabId)) return;
    setGroupDragState({
      documentViewer,
      dragEl: el,
      draggedAnnotation: annotation,
      event,
      group: documentViewer.getAnnotationManager().getGroupAnnotations(annotation),
      offsetLeft: el.parentElement.offsetLeft,
      offsetTop: el.parentElement.offsetTop,
      tabId,
    });
  });

  return el;
}

export function useRegisterFocusedAnnotationListener(): void {
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useCurrentTabIdOrThrow();
  const focusedField = useVariablePanelFocusedField(tabId);
  const prevFocusedField = usePreviousDistinct(focusedField);

  useEffect(() => {
    if (!documentViewer) return;

    const annotationManager = documentViewer.getAnnotationManager();
    const annotations = annotationManager.getAnnotationsList();
    if (focusedField) {
      const focusedAnnotations = annotations.filter(annot =>
        isPdfWidgetFocused(annot, focusedField)
      );
      focusedAnnotations.forEach(focusedAnnotation => {
        annotationManager.drawAnnotations({
          majorRedraw: true,
          pageNumber: focusedAnnotation.PageNumber,
        });
      });

      return;
    }

    if (!prevFocusedField) return;

    const prevFocusedAnnotations = annotations.filter(annot =>
      isPdfWidgetFocused(annot, prevFocusedField)
    );
    prevFocusedAnnotations.forEach(prevFocusedAnnotation => {
      annotationManager.drawAnnotations({
        majorRedraw: true,
        pageNumber: prevFocusedAnnotation.PageNumber,
      });
    });
  }, [documentViewer, focusedField, prevFocusedField]);
}

const annotationsLoadedVar = makeVar<ViewerDocInfo | null>(null);

export function useAreAnnotationsLoaded(): boolean {
  const { document } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const viewerDocInfo = useReactiveVar(annotationsLoadedVar);

  return (
    documentId === viewerDocInfo?.documentId &&
    document?.latestVersion.id === viewerDocInfo?.fileVersionId
  );
}

// Grows multiline fields on a page whose text is clipped to fit it.
async function fitClippedMultilineFields(
  documentViewer: Core.DocumentViewer,
  pageNum: number
): Promise<boolean> {
  const annotationManager = documentViewer.getAnnotationManager();
  let didFit = false;

  for (const widget of annotationManager.getAnnotationsList()) {
    if (widget.PageNumber !== pageNum) continue;
    if (!(widget instanceof pdfTronCore.Annotations.TextWidgetAnnotation)) continue;
    if (!getIsAlineFieldName(widget.fieldName)) continue;
    if (!widget.getField().flags?.get(pdfTronCore.Annotations.WidgetFlags.MULTILINE)) continue;

    const textEl = widget.innerElement;
    if (!(textEl instanceof HTMLTextAreaElement)) continue;
    if (textEl.scrollHeight <= textEl.clientHeight + 1) continue;

    if (growMultilineWidgetToContent(documentViewer, widget)) didFit = true;
  }

  if (didFit) await annotationManager.drawAnnotations({ majorRedraw: true, pageNumber: pageNum });
  return didFit;
}

// Grows a multiline widget's height to fit its content (never shrinks). Returns if it changed.
function growMultilineWidgetToContent(
  documentViewer: Core.DocumentViewer,
  widget: Core.Annotations.WidgetAnnotation
): boolean {
  const fitHeightPx = getMultilineFitHeightPx(documentViewer, widget);
  if (fitHeightPx == null || fitHeightPx <= Math.round(widget.Height)) return false;

  const userSetHeightRaw = widget.getCustomData(PDFCustomDataKey.userSetAnnotHeight);
  if (userSetHeightRaw !== '') {
    const userSetHeight = Number(userSetHeightRaw);
    if (Number.isFinite(userSetHeight) && fitHeightPx > userSetHeight) return false;
  }

  widget.setHeight(fitHeightPx);
  widget.setCustomData(PDFCustomDataKey.userSetAnnotHeight, String(fitHeightPx));
  return true;
}

/**
 * Recreates a field's widgets as multiline — PDFTron only picks <input> vs <textarea> at
 * element-creation time. The old widget is spliced out of field.widgets (deleteAnnotation
 * leaves it there, and a redraw would re-add a duplicate).
 */
function convertTextFieldToMultiline(
  documentViewer: Core.DocumentViewer,
  field: Core.Annotations.Forms.Field
): Core.Annotations.TextWidgetAnnotation[] {
  if (field.flags?.get(pdfTronCore.Annotations.WidgetFlags.MULTILINE)) return [];

  const annotationManager = documentViewer.getAnnotationManager();
  field.flags?.set(pdfTronCore.Annotations.WidgetFlags.MULTILINE, true);

  const newWidgets: Core.Annotations.TextWidgetAnnotation[] = [];
  for (const oldWidget of [...(field.widgets ?? [])]) {
    if (!(oldWidget instanceof pdfTronCore.Annotations.TextWidgetAnnotation)) continue;

    const newWidget = new pdfTronCore.Annotations.TextWidgetAnnotation(field, {});
    Object.values(PDFCustomDataKey).forEach(dataKey => {
      if (dataKey === PDFCustomDataKey.signatureImgId) return;
      newWidget.setCustomData(dataKey, oldWidget.getCustomData(dataKey));
    });
    newWidget.PageNumber = oldWidget.PageNumber;
    newWidget.X = oldWidget.X;
    newWidget.Y = oldWidget.Y;
    newWidget.Width = oldWidget.Width;
    newWidget.Height = oldWidget.Height;
    newWidget.rotation = getPageRotationDeg(documentViewer, newWidget.getPageNumber());

    annotationManager.deleteAnnotation(oldWidget, { force: true });
    newWidget.Id = oldWidget.Id;
    annotationManager.addAnnotation(newWidget);

    const oldIndex = field.widgets?.indexOf(oldWidget) ?? -1;
    if (oldIndex !== -1) field.widgets?.splice(oldIndex, 1);

    newWidgets.push(newWidget);
  }
  return newWidgets;
}

export function useZoomLevel() {
  const tabId = useCurrentTabIdOrThrow();
  const {
    viewer: { setViewerZoomLevel },
  } = useEditorTabStateActionsForTabId(tabId);
  const documentViewer = useMaybeDocumentViewer();
  const viewerElement = documentViewer?.getViewerElement() ?? null;
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const scale = useEditorTabStateSelectorForTabId(tabId, state => state.viewer.zoomLevel);

  useEffect(() => {
    if (!documentViewer) return;

    const handleDocumentLoaded = () => setIsDocumentLoaded(true);
    const handleDocumentUnloaded = () => setIsDocumentLoaded(false);
    documentViewer.addEventListener('documentLoaded', handleDocumentLoaded);
    documentViewer.addEventListener('documentUnloaded', handleDocumentUnloaded);

    return () => {
      documentViewer.removeEventListener('documentLoaded', handleDocumentLoaded);
      documentViewer.removeEventListener('documentUnloaded', handleDocumentUnloaded);
    };
  }, [documentViewer]);

  useEffect(() => {
    if (documentViewer && isDocumentLoaded) documentViewer.zoomTo(scale);
  }, [documentViewer, isDocumentLoaded, scale]);

  useEffect(() => {
    if (!viewerElement) return;

    function handleWheel(event: WheelEvent) {
      if (!documentViewer) return;

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const currentZoom = documentViewer.getZoomLevel();
        const zoomIncrement = -event.deltaY * 0.01;
        const newZoom = currentZoom + zoomIncrement;
        const clampedZoom = Math.max(0.5, Math.min(5.0, newZoom));

        setViewerZoomLevel(clampedZoom);
      }
    }

    viewerElement.parentElement?.addEventListener('wheel', handleWheel);
    return () => viewerElement.parentElement?.removeEventListener('wheel', handleWheel);
  }, [documentViewer, setViewerZoomLevel, viewerElement]);
}

export function useRegisterAnnotationListeners(): void {
  const tabId = useCurrentTabIdOrThrow();
  const documentViewer = useMaybeDocumentViewer();
  const { debouncedSyncAnnotations } = useSyncAnnotations(tabId);
  const {
    viewer: { incrementViewerAnnotationsChangedCount },
  } = useEditorTabStateActionsForTabId(tabId);

  useEffect(() => {
    if (!documentViewer) return;

    const annotationManager = documentViewer.getAnnotationManager();
    const handleAnnotationsLoaded = async () => {
      annotationsLoadedVar(getViewerDocInfo(documentViewer));
      await flattenExternalSignedSignatureAppearances(documentViewer);
    };
    const handleDocumentUnloaded = () => annotationsLoadedVar(null);
    const handleAnnotationChanged = (
      _annotations: Core.Annotations.Annotation[],
      action: string,
      info: { imported: boolean }
    ) => {
      if (info.imported || action === 'add' || action === 'delete')
        incrementViewerAnnotationsChangedCount();
    };
    const handleAnnotationsDrawn = async (pageNum: number) => {
      hideFlattenedSignatureWidgetElements(annotationManager, pageNum);
      const pageWidgetContainer = getPageWidgetContainer(documentViewer, pageNum);
      let didAttach = false;
      // Allows dragging of WidgetAnnotation HTML elements across pages.
      if (pageWidgetContainer) {
        pageWidgetContainer.style.overflow = 'unset';
        didAttach = syncPageWidgetElements(annotationManager, pageNum, pageWidgetContainer);
      }

      if (didAttach)
        await annotationManager.drawAnnotations({ majorRedraw: true, pageNumber: pageNum });

      const didFit = await fitClippedMultilineFields(documentViewer, pageNum);
      if (didFit) await debouncedSyncAnnotations(documentViewer);
    };
    const handleFieldValueChanged = async (field: Core.Annotations.Forms.Field, _value: string) => {
      const firstWidget = (field.widgets ?? [])[0];
      let isMultiLine = Boolean(field.flags?.get(pdfTronCore.Annotations.WidgetFlags.MULTILINE));
      const isReadOnly = Boolean(field.flags?.get(pdfTronCore.Annotations.WidgetFlags.READ_ONLY));
      // Panel-driven READ_ONLY fields update via setValue; skip multiline conversion here
      // (recreating the widget races panel sync and clears the visible input value).
      if (
        !isMultiLine &&
        !isReadOnly &&
        firstWidget instanceof pdfTronCore.Annotations.TextWidgetAnnotation &&
        getIsAlineFieldName(firstWidget.fieldName)
      ) {
        const newWidgets = convertTextFieldToMultiline(documentViewer, field);
        isMultiLine = true;
        // Draw first: recreated widgets have no element until drawn, and we measure height below.
        for (const pageNumber of new Set(newWidgets.map(widget => widget.PageNumber))) {
          await annotationManager.drawAnnotations({ majorRedraw: true, pageNumber });
        }
      }
      const widgetAnnotations = field.widgets ?? [];

      if (field.type === PDFFieldType.Btn) {
        if (!(await lockSquareCheckboxWidgetsAndRedraw(annotationManager, field))) return;

        const callbacks = checkboxPdfFieldCallbacksByViewer.get(documentViewer);
        const disabledReason = await callbacks?.getDisabledReason(field.name);
        if (disabledReason) {
          await callbacks?.revertUnauthorizedChange(field, disabledReason);
          return;
        }
        await callbacks?.sync(field);
        return debouncedSyncAnnotations(documentViewer);
      }

      // No need to autosize field if it's multiline...
      if (isMultiLine) {
        await Promise.all(
          widgetAnnotations.map(async widget => {
            const neededHeightPx = getMultilineFitHeightPx(documentViewer, widget);
            if (neededHeightPx == null || neededHeightPx === Math.round(widget.Height)) return;

            const oldHeight = widget.Height;
            widget.setHeight(neededHeightPx);
            widget.setCustomData(PDFCustomDataKey.userSetAnnotHeight, String(neededHeightPx));
            // Keep the visual top-left fixed on rotated pages when the field grows/shrinks on type.
            anchorResizedFieldTopLeft(documentViewer, widget, widget.Width, oldHeight);
            await annotationManager.drawAnnotations({
              majorRedraw: true,
              pageNumber: widget.PageNumber,
            });
          })
        );
        return debouncedSyncAnnotations(documentViewer);
      }

      // Autosize single-line field.
      await Promise.all(
        widgetAnnotations.map(async widget => {
          if (!widget.innerElement) return;
          // Canned widgets have their own fit via `applyCannedWidgetAutoFit`;
          // this grow-only autosize would defeat the shrink-font-first path.
          if (widget.getCustomData(PDFCustomDataKey.cannedFieldId)) return;
          const canvas = createAlineCanvas();
          const { font } = getComputedStyle(widget.innerElement);
          const context = canvas.getContext('2d');
          context.font = font;

          const textWidthPx = context.measureText(String(field.getValue())).width;
          // Need to add 8 to give a bit of padding to text so it fits every time...
          const minWidgetWidthNeededForText =
            textWidthPx +
            defaultPDFFieldAttributes.paddingLeftPx +
            defaultPDFFieldAttributes.paddingRightPx +
            8;
          const pageWidthPx = documentViewer.getPageWidth(widget.getPageNumber());
          const maxWidgetWidthPx = pageWidthPx - widget.X;
          const userSetAnnotWidth = parseInt(
            widget.getCustomData(PDFCustomDataKey.userSetAnnotWidth)
          );
          const safeUserSetAnnotWidth = !isNaN(userSetAnnotWidth)
            ? userSetAnnotWidth
            : defaultPDFFieldAttributes.nonSigFieldWidthPx;
          if (minWidgetWidthNeededForText !== widget.Width) {
            widget.setWidth(
              minWidgetWidthNeededForText <= safeUserSetAnnotWidth
                ? safeUserSetAnnotWidth
                : minWidgetWidthNeededForText >= maxWidgetWidthPx
                  ? maxWidgetWidthPx
                  : minWidgetWidthNeededForText
            );
            await annotationManager.drawAnnotations({
              majorRedraw: true,
              pageNumber: widget.PageNumber,
            });
          }
        })
      );

      await debouncedSyncAnnotations(documentViewer);
    };

    documentViewer.addEventListener('documentUnloaded', handleDocumentUnloaded);
    documentViewer.addEventListener('annotationsLoaded', handleAnnotationsLoaded);

    annotationManager.addEventListener('annotationChanged', handleAnnotationChanged);
    annotationManager.addEventListener('annotationsDrawn', handleAnnotationsDrawn);
    // See https://docs.apryse.com/api/web/Core.AnnotationManager.html#event:fieldChanged__anchor for types of callback.
    annotationManager.addEventListener('fieldChanged', handleFieldValueChanged);

    return () => {
      annotationManager.removeEventListener('fieldChanged', handleFieldValueChanged);
      documentViewer.removeEventListener('documentUnloaded', handleDocumentUnloaded);
      documentViewer.removeEventListener('annotationsLoaded', handleAnnotationsLoaded);
      annotationManager.removeEventListener('annotationChanged', handleAnnotationChanged);
      annotationManager.removeEventListener('annotationsDrawn', handleAnnotationsDrawn);
    };
  }, [documentViewer, debouncedSyncAnnotations, incrementViewerAnnotationsChangedCount]);
}

/**
 * WebViewer v12 mounts page widget containers under `getViewerElement()`. Fall
 * back to the scroll view for older layouts / transitional states.
 */
function getPageWidgetContainer(
  documentViewer: Core.DocumentViewer,
  pageNum: number
): HTMLElement | null {
  const selector = `#pageWidgetContainer${pageNum}`;
  return (
    documentViewer.getViewerElement()?.querySelector<HTMLElement>(selector) ??
    documentViewer.getScrollViewElement()?.querySelector<HTMLElement>(selector) ??
    null
  );
}

interface DragHandleState {
  draggedEl: {
    annotation: Core.Annotations.WidgetAnnotation;
    parentEl: HTMLElement;
  };
  isScaleResize: boolean;
  originalHeightPx: number;
  // PDFTron's transform on a rotated page; preserved so the scale preview doesn't drop rotation.
  originalTransform: string;
  originalTransformOrigin: string;
  originalWidthPx: number;
  x: number;
  y: number;
}

const dragHandleStateVar = makeVar<DragHandleState | null>(null);

export function useResizeCursorOverride(): void {
  const dragHandleState = useReactiveVar(dragHandleStateVar);
  useOverrideGlobalCursor(
    dragHandleState ? (dragHandleState.isScaleResize ? 'nwse-resize' : 'ew-resize') : null
  );
}

export function useRegisterDragAnnotationListeners(): void {
  const tabId = useCurrentTabIdOrThrow();
  const documentViewer = useMaybeDocumentViewer();
  const { syncAnnotations } = useSyncAnnotations(tabId);
  const { isAtVersionHistoryRoute } = useIsAtVersionHistoryRoute();

  useEffect(() => {
    if (!documentViewer) return;

    const annotationManager = documentViewer.getAnnotationManager();

    const attachDragHandle = (
      parentEl: HTMLElement,
      annotation: Core.Annotations.WidgetAnnotation,
      positionStyle: React.CSSProperties,
      isScaleResize: boolean
    ) => {
      const dragHandleEl = document.createElement('div');
      dragHandleEl.dataset.cy = Select.document.fields.dragHandle;
      Object.assign(
        dragHandleEl.style,
        kebabCaseCSS({
          backgroundColor: IconColors.Primary,
          borderRadius: '50%',
          height: '8px',
          position: 'absolute',
          right: '-4px',
          width: '8px',
          ...positionStyle,
        })
      );
      dragHandleEl.addEventListener('mousedown', event => {
        const userState = getEditorUserStateForTabId(tabId);
        // Default to true so fields can't be dragged during signing.
        const isInSigningCeremony = userState?.isInSigningCeremony ?? true;
        if (!dragHandleEl.parentElement || isInSigningCeremony) return;
        const { height, width } = dragHandleEl.parentElement.getBoundingClientRect();
        dragHandleStateVar({
          draggedEl: { annotation, parentEl: dragHandleEl.parentElement },
          isScaleResize,
          originalHeightPx: height,
          originalTransform: dragHandleEl.parentElement.style.transform,
          originalTransformOrigin: dragHandleEl.parentElement.style.transformOrigin,
          originalWidthPx: width,
          x: event.pageX,
          y: event.pageY,
        });
      });
      parentEl.appendChild(dragHandleEl);
    };
    const decorateFieldWidgetsOnDraw = (pageNumber: number) => {
      const userState = getEditorUserStateForTabId(tabId);
      const isInSigningCeremony = userState?.isInSigningCeremony ?? true;
      const canAttachDragHandles = !isInSigningCeremony && !isAtVersionHistoryRoute;

      const annotations = annotationManager.getAnnotationsList();
      const drawnAnnotations = annotations.filter(
        annotation => annotation.PageNumber === pageNumber
      );
      drawnAnnotations.forEach(annotation => {
        const isWidget = annotation instanceof pdfTronCore.Annotations.WidgetAnnotation;
        if (!isWidget || !annotation.innerElement?.parentElement) return;

        if (annotation instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation) return;

        const isAlineAnnotation = getIsAlineFieldName(annotation.fieldName);
        if (!isAlineAnnotation) return;

        const parentEl = annotation.innerElement.parentElement;

        const isSignature = annotation instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation;
        if (isSignature) {
          setSignatureWidgetVisualState(parentEl, annotation.Width);
        }

        if (isAtVersionHistoryRoute) {
          Object.assign(parentEl.style, {
            borderWidth: `${defaultPDFFieldAttributes.borderSizePx}px`,
            cursor: 'default',
            pointerEvents: 'none',
          });
        }

        if (!canAttachDragHandles) return;

        const parentHasDragHandle = [...parentEl.childNodes.values()].some(
          el => el instanceof HTMLElement && el.dataset.cy === Select.document.fields.dragHandle
        );
        if (parentHasDragHandle) return;

        if (isSignature) {
          attachDragHandle(parentEl, annotation, { bottom: '-4px', cursor: 'nwse-resize' }, false);
        } else {
          attachDragHandle(
            parentEl,
            annotation,
            {
              backgroundColor: 'transparent',
              borderRadius: '0',
              bottom: '0',
              cursor: 'ew-resize',
              height: 'auto',
              top: '0',
              width: '8px',
            },
            false
          );
          attachDragHandle(
            parentEl,
            annotation,
            { cursor: 'ew-resize', top: '50%', transform: 'translateY(-50%)' },
            false
          );
          attachDragHandle(
            parentEl,
            annotation,
            { bottom: '-4px', cursor: 'nwse-resize', zIndex: '1' },
            true
          );
        }
      });

      if (!dragStateVar().draggedEl) {
        for (const annotation of drawnAnnotations) {
          if (!isCheckboxOptionLabel(annotation)) continue;
          const primary = getCheckboxGroupPrimary(
            annotationManager.getGroupAnnotations(annotation)
          );
          if (primary) {
            wireCheckboxGroupHitOverlay({
              annotationManager,
              documentViewer,
              labelAnnotation: annotation,
              primary,
              tabId,
            });
          }
        }
      }
    };

    const handleDragHandleMove = (event: MouseEvent) => {
      // No dragging when viewing version history.
      if (isAtVersionHistoryRoute) return;

      const dragHandleState = dragHandleStateVar();
      if (!dragHandleState) return;

      const {
        draggedEl: { annotation, parentEl },
      } = dragHandleState;
      const field = annotation.getField();
      const zoom = documentViewer.getZoomLevel();
      const isSignature = annotation instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation;
      const isMultiLine = Boolean(field.flags?.get(pdfTronCore.Annotations.WidgetFlags.MULTILINE));

      if (dragHandleState.isScaleResize) {
        const { deltaX } = getDragHandleDeltas(documentViewer, {
          dragHandleState,
          event,
          eventType: 'mousemove',
        });
        const minWidthScale =
          convertDimensionUsingZoom(defaultPDFFieldAttributes.nonSigFieldMinWidthPx, {
            direction: ZoomConvertDir.To,
            zoom,
          }) / dragHandleState.originalWidthPx;
        const minFontScale =
          defaultPDFFieldAttributes.nonSigFieldMinFontPx /
          (field.font?.size ?? defaultPDFFieldAttributes.fontSizePx);
        const minScale = Math.max(minWidthScale, minFontScale);
        const maxScale = getMaxFieldScale(documentViewer, annotation);
        const scale = Math.min(
          Math.max(minScale, maxScale),
          Math.max(
            minScale,
            (dragHandleState.originalWidthPx + deltaX) / dragHandleState.originalWidthPx
          )
        );
        const { originalTransform, originalTransformOrigin } = dragHandleState;
        // Compose the scale onto PDFTron's existing transform so a rotated field keeps its
        // rotation during the preview instead of flipping to unrotated.
        parentEl.style.transformOrigin = originalTransform ? originalTransformOrigin : 'top left';
        parentEl.style.transform = originalTransform
          ? `${originalTransform} scale(${scale})`
          : `scale(${scale})`;
        return;
      }

      const { deltaX } = getDragHandleDeltas(documentViewer, {
        dragHandleState,
        event,
        eventType: 'mousemove',
      });
      const minWidthPx = isSignature
        ? defaultPDFFieldAttributes.sigFieldMinWidthPx
        : defaultPDFFieldAttributes.nonSigFieldMinWidthPx;
      // Ensure width doesn't shrink below min width.
      const maxWidthScreenPx = convertDimensionUsingZoom(
        getMaxFieldWidthPdf(documentViewer, annotation),
        { direction: ZoomConvertDir.To, zoom }
      );
      const safeWidthPx = Math.min(
        maxWidthScreenPx,
        Math.max(
          convertDimensionUsingZoom(minWidthPx, {
            direction: ZoomConvertDir.To,
            zoom,
          }),
          dragHandleState.originalWidthPx + deltaX
        )
      );
      // Ensure sig width doesn't expand beyond default sig width.
      const sigWidthPx = Math.min(
        convertDimensionUsingZoom(defaultPDFFieldAttributes.sigFieldWidthPx, {
          direction: ZoomConvertDir.To,
          zoom,
        }),
        safeWidthPx
      );
      const newAnnotWidthPx = isSignature ? sigWidthPx : safeWidthPx;
      // Keep sig height a consistent aspect ratio w/ sig width.
      const sigHeightPx = newAnnotWidthPx / defaultPDFFieldAttributes.sigFieldAspectRatio;

      parentEl.style.width = `${newAnnotWidthPx}px`;

      const multilineFitScreenPx = isMultiLine
        ? getMultilineContentHeightScreenPx(annotation)
        : null;
      const newAnnotHeightPx = isMultiLine
        ? (multilineFitScreenPx ?? dragHandleState.originalHeightPx)
        : isSignature
          ? sigHeightPx
          : dragHandleState.originalHeightPx;
      parentEl.style.height = `${newAnnotHeightPx}px`;
      if (isSignature) {
        setSignatureWidgetVisualState(
          dragHandleState.draggedEl.parentEl,
          convertDimensionUsingZoom(newAnnotWidthPx, { direction: ZoomConvertDir.From, zoom })
        );
      }
    };
    const handleMouseUp = async (event: MouseEvent) => {
      // No dragging when viewing version history.
      if (isAtVersionHistoryRoute) return;

      const dragHandleState = dragHandleStateVar();
      if (!dragHandleState) return;

      let { parentEl } = dragHandleState.draggedEl;
      let annotation = dragHandleState.draggedEl.annotation;
      const field = annotation.getField();
      const isSignature = annotation instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation;
      let isMultiLine = Boolean(field.flags?.get(pdfTronCore.Annotations.WidgetFlags.MULTILINE));

      if (dragHandleState.isScaleResize) {
        const oldWidth = annotation.Width;
        const oldHeight = annotation.Height;
        const rawDeltaX = event.pageX - dragHandleState.x;
        const zoom = documentViewer.getZoomLevel();

        if (
          !isMultiLine &&
          annotation instanceof pdfTronCore.Annotations.TextWidgetAnnotation &&
          getIsAlineFieldName(annotation.fieldName)
        ) {
          const draggedPage = annotation.PageNumber;
          const newWidgets = convertTextFieldToMultiline(documentViewer, field);
          annotation = newWidgets.find(widget => widget.PageNumber === draggedPage) ?? annotation;
          isMultiLine = true;
          parentEl = annotation.innerElement?.parentElement ?? parentEl;
        }

        const minWidthScale =
          convertDimensionUsingZoom(defaultPDFFieldAttributes.nonSigFieldMinWidthPx, {
            direction: ZoomConvertDir.To,
            zoom,
          }) / dragHandleState.originalWidthPx;
        const minFontScale =
          defaultPDFFieldAttributes.nonSigFieldMinFontPx /
          (field.font?.size ?? defaultPDFFieldAttributes.fontSizePx);
        const minScale = Math.max(minWidthScale, minFontScale);
        const maxScale = getMaxFieldScale(documentViewer, annotation);
        const scale = Math.min(
          Math.max(minScale, maxScale),
          Math.max(
            minScale,
            (dragHandleState.originalWidthPx + rawDeltaX) / dragHandleState.originalWidthPx
          )
        );

        dragHandleStateVar(null);
        parentEl.style.transform = dragHandleState.originalTransform;
        parentEl.style.transformOrigin = dragHandleState.originalTransformOrigin;

        const newWidthPx = convertDimensionUsingZoom(dragHandleState.originalWidthPx * scale, {
          direction: ZoomConvertDir.From,
          zoom,
        });
        const scaledHeightPx = convertDimensionUsingZoom(dragHandleState.originalHeightPx * scale, {
          direction: ZoomConvertDir.From,
          zoom,
        });
        annotation.setWidth(newWidthPx);
        annotation.setCustomData(PDFCustomDataKey.userSetAnnotWidth, String(newWidthPx));

        if (field.font) {
          field.font.size *= scale;
          field.refreshAppearances();
        }

        annotation.setHeight(scaledHeightPx);
        annotation.setCustomData(PDFCustomDataKey.userSetAnnotHeight, String(scaledHeightPx));

        anchorResizedFieldTopLeft(documentViewer, annotation, oldWidth, oldHeight);

        await annotationManager.drawAnnotations({
          majorRedraw: true,
          pageNumber: annotation.PageNumber,
        });
        await syncAnnotations(documentViewer);
        return;
      }

      const { deltaX } = getDragHandleDeltas(documentViewer, {
        dragHandleState,
        event,
        eventType: 'mouseup',
      });

      const minWidthPx = isSignature
        ? defaultPDFFieldAttributes.sigFieldMinWidthPx
        : defaultPDFFieldAttributes.nonSigFieldMinWidthPx;
      // Ensure width doesn't shrink below min width.
      const maxWidthPdf = getMaxFieldWidthPdf(documentViewer, annotation);
      const safeWidthPx = Math.min(maxWidthPdf, Math.max(minWidthPx, annotation.Width + deltaX));
      // Ensure signature cannot expand beyond default width that is set.
      const sigWidthPx = Math.min(defaultPDFFieldAttributes.sigFieldWidthPx, safeWidthPx);
      const newAnnotWidthPx = isSignature ? sigWidthPx : safeWidthPx;
      // Keep sig height a consistent aspect ratio.
      const sigHeightPx = newAnnotWidthPx / defaultPDFFieldAttributes.sigFieldAspectRatio;

      const oldWidth = annotation.Width;
      const oldHeight = annotation.Height;
      dragHandleStateVar(null);
      annotation.setWidth(newAnnotWidthPx);
      annotation.setCustomData(PDFCustomDataKey.userSetAnnotWidth, String(newAnnotWidthPx));

      if (!isMultiLine) {
        const newAnnotHeightPx = isSignature ? sigHeightPx : annotation.Height;
        annotation.setHeight(newAnnotHeightPx);
        annotation.setCustomData(PDFCustomDataKey.userSetAnnotHeight, String(newAnnotHeightPx));
      }

      anchorResizedFieldTopLeft(documentViewer, annotation, oldWidth, oldHeight);

      await annotationManager.drawAnnotations({
        majorRedraw: true,
        pageNumber: annotation.PageNumber,
      });
      // Save new width, height to server on mouseup.
      await syncAnnotations(documentViewer);
    };

    document.body.addEventListener('mouseup', handleMouseUp);
    documentViewer.addEventListener('mouseMove', handleDragHandleMove);
    annotationManager.addEventListener('annotationsDrawn', decorateFieldWidgetsOnDraw);

    return () => {
      document.body.removeEventListener('mouseup', handleMouseUp);
      documentViewer.removeEventListener('mouseMove', handleDragHandleMove);
      annotationManager.removeEventListener('annotationsDrawn', decorateFieldWidgetsOnDraw);
    };
  }, [documentViewer, isAtVersionHistoryRoute, syncAnnotations, tabId]);

  useEffect(() => {
    if (!documentViewer) return;

    let checkboxGroupDrawRafId = 0;
    let checkboxGroupDrawPageNum = 0;

    const scheduleCheckboxGroupAnnotationRedraw = (pageNumber: number): void => {
      checkboxGroupDrawPageNum = pageNumber;
      if (checkboxGroupDrawRafId) return;

      checkboxGroupDrawRafId = requestAnimationFrame(() => {
        checkboxGroupDrawRafId = 0;
        documentViewer.getAnnotationManager().drawAnnotations({
          majorRedraw: true,
          pageNumber: checkboxGroupDrawPageNum,
        });
      });
    };

    const handleMouseMove = (event: MouseEvent) => {
      // No dragging when viewing version history.
      if (isAtVersionHistoryRoute) return;

      const state = dragStateVar();
      if (!state.draggedEl?.el.parentElement) return;
      /**
       * Multiple tabs each attach this listener to document.body and share dragStateVar.
       * Only the listener whose documentViewer owns the dragged annotation should react.
       */
      if (!documentViewer.getAnnotationManager().getAnnotationById(state.draggedEl.annotation.Id))
        return;

      const coords = getPDFPageCoords(documentViewer, { event });
      if (
        !coords ||
        isCheckboxGroupClick(coords.windowCoords, state.mouseDownX, state.mouseDownY)
      ) {
        return;
      }

      const dragPoints = getDragPoints({ coords, documentViewer, event, state });
      if (!dragPoints) return;

      const pageDeltaX = dragPoints.pageCoords.x - state.pageMouseDownX;
      const pageDeltaY = dragPoints.pageCoords.y - state.pageMouseDownY;
      const members = state.groupMemberStartPositions ?? [];
      if (isCheckboxGroupDrag(state)) {
        if (getEditorUserStateForTabId(tabId)?.isInSigningCeremony) return;
        if (document.body.style.cursor !== 'grabbing') setCheckboxGroupCursor(state, 'grabbing');
      } else {
        state.draggedEl.el.style.cursor = 'grabbing';
      }

      if (findCheckboxOptionLabelMember(members)) {
        for (const member of members) {
          member.annotation.setX(member.startX + pageDeltaX);
          member.annotation.setY(member.startY + pageDeltaY);
        }

        scheduleCheckboxGroupAnnotationRedraw(
          dragPoints.eventPageNum || state.draggedEl.annotation.PageNumber
        );
        return;
      }

      const deltaLeft = dragPoints.offsetLeftPx - state.offsetLeft;
      const deltaTop = dragPoints.offsetTopPx - state.offsetTop;

      // Only count it as a drag once the field actually moves past a small threshold, so a click
      // with tiny jitter still focuses the field instead of being swallowed as a no-op drag.
      const movedPx = Math.hypot(
        dragPoints.offsetLeftPx - state.offsetLeft,
        dragPoints.offsetTopPx - state.offsetTop
      );
      if (movedPx > FIELD_DRAG_MOVE_THRESHOLD_PX) didDragMoveField = true;

      state.draggedEl.el.parentElement.style.top = `${dragPoints.offsetTopPx}px`;
      state.draggedEl.el.parentElement.style.left = `${dragPoints.offsetLeftPx}px`;

      for (const t of state.groupDragTargets ?? []) {
        if (t.parentEl === state.draggedEl.el.parentElement) continue;
        t.parentEl.style.left = `${t.startLeft + deltaLeft}px`;
        t.parentEl.style.top = `${t.startTop + deltaTop}px`;
      }
    };
    const handleMouseUp = async (event: MouseEvent) => {
      // No dragging when viewing version history.
      if (isAtVersionHistoryRoute) return;

      const state = dragStateVar();

      /**
       * Multiple tabs each attach mouseup to document.body and share dragStateVar.
       * If the dragged annotation doesn't belong to THIS documentViewer, bail BEFORE
       * clearing the state so the owning listener can still commit the drop.
       */
      if (
        state.draggedEl &&
        !documentViewer.getAnnotationManager().getAnnotationById(state.draggedEl.annotation.Id)
      )
        return;

      setCheckboxGroupCursor(state, null);
      if (checkboxGroupDrawRafId) {
        cancelAnimationFrame(checkboxGroupDrawRafId);
        checkboxGroupDrawRafId = 0;
      }

      const {
        checkboxFieldValueBeforeDrag,
        draggedEl,
        groupMemberStartPositions: groupStarts = [],
        mouseDownX,
        mouseDownY,
        pageMouseDownX,
        pageMouseDownY,
      } = state;
      if (!draggedEl) return;

      const { annotation } = draggedEl;
      /**
       * Note: Important to clear drag state FIRST (but after reading it on the line above) so that drag
       * listeners (e.g., mousemove) don't change position of the HTML widget.
       */
      setDragState(defaultDragState);

      const coords = getPDFPageCoords(documentViewer, { event });
      if (!coords) return;

      const { eventPageNum, pageCoords } = coords;
      const targetPageNum = eventPageNum !== 0 ? eventPageNum : annotation.PageNumber;
      const deltaY = pageCoords.y - pageMouseDownY;
      const deltaX = pageCoords.x - pageMouseDownX;
      const moved = !isCheckboxGroupClick(coords.windowCoords, mouseDownX, mouseDownY);

      if (annotation instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation) {
        // Apryse toggles on the click event after mouseup. Suppress it so we toggle at most once.
        suppressNextClick();
        if (!moved) {
          // Apryse may also toggle before mouseup (fieldChanged). Skip manual toggle when it did.
          const currentValue = String(annotation.getField()?.getValue() ?? '');
          if (currentValue === (checkboxFieldValueBeforeDrag ?? '')) {
            await activateCheckboxOptionGroup(documentViewer, annotation);
          }
          return;
        }
        annotation.getField()?.setValue(checkboxFieldValueBeforeDrag ?? '');
      } else if (!moved) {
        return;
      }

      const annotPage = annotation.PageNumber;

      const offsetInFieldX = pageMouseDownX - annotation.X;
      const offsetInFieldY = pageMouseDownY - annotation.Y;
      const newFieldTopLeftX = coords.pageCoords.x - offsetInFieldX;
      const newFieldTopLeftY = coords.pageCoords.y - offsetInFieldY;

      const destPageWidth = documentViewer.getPageWidth(targetPageNum);
      const destPageHeight = documentViewer.getPageHeight(targetPageNum);
      const destRotation = documentViewer.getCompleteRotation(targetPageNum);
      const isDestRotated90or270 =
        destRotation === pdfTronCore.PageRotation.E_90 ||
        destRotation === pdfTronCore.PageRotation.E_270;
      const fieldExtentX = isDestRotated90or270 ? annotation.getHeight() : annotation.getWidth();
      const fieldExtentY = isDestRotated90or270 ? annotation.getWidth() : annotation.getHeight();
      const safeX = Math.min(destPageWidth - fieldExtentX, Math.max(0, newFieldTopLeftX));
      const safeY = Math.min(destPageHeight - fieldExtentY, Math.max(0, newFieldTopLeftY));

      const noChange =
        targetPageNum === annotPage && safeX === annotation.X && safeY === annotation.Y;
      if (noChange) {
        if (moved) {
          const annotationManager = documentViewer.getAnnotationManager();
          await annotationManager.drawAnnotations({
            majorRedraw: true,
            pageNumber: annotation.PageNumber,
          });
          await syncAnnotations(documentViewer);
        }
        return;
      }

      const pageWidth = documentViewer.getPageWidth(targetPageNum);
      const pageHeight = documentViewer.getPageHeight(targetPageNum);
      const members = groupStarts ?? [];
      const primaryStart = members.find(member => member.annotation === annotation) ?? {
        startX: annotation.X,
        startY: annotation.Y,
      };

      const labelStart = findCheckboxOptionLabelMember(members);
      const { x: clampSafeX, y: clampSafeY } = clampCheckboxGroupPrimaryPagePosition({
        desiredPrimaryX: primaryStart.startX + deltaX,
        desiredPrimaryY: primaryStart.startY + deltaY,
        fieldExtentX,
        fieldExtentY,
        labelStart,
        pageHeight,
        pageWidth,
        primaryStart,
      });

      const annotationManager = documentViewer.getAnnotationManager();
      const group = annotationManager.getGroupAnnotations(annotation);
      const startByAnnotation = new Map(members.map(start => [start.annotation, start]));

      for (const ann of group) {
        const start = startByAnnotation.get(ann);
        if (!start) continue;

        if (ann === annotation) {
          ann.setX(clampSafeX);
          ann.setY(clampSafeY);
        } else {
          ann.setX(clampSafeX + (start.startX - primaryStart.startX));
          ann.setY(clampSafeY + (start.startY - primaryStart.startY));
        }
        ann.setPageNumber(targetPageNum);
      }
      await annotationManager.drawAnnotations({
        majorRedraw: true,
        pageNumber: targetPageNum,
      });
      // Save new X, Y to server on mouseup.  For some reason, this isn't caught in the annotation event listeners...
      await syncAnnotations(documentViewer);
    };

    document.body.addEventListener('mouseup', handleMouseUp);
    document.body.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.body.removeEventListener('mouseup', handleMouseUp);
      document.body.removeEventListener('mousemove', handleMouseMove);
    };
  }, [documentViewer, isAtVersionHistoryRoute, syncAnnotations, tabId]);
}

/**
 * Suppress the next click event to occur within CHECKBOX_CLICK_SUPPRESS_MS.
 */
function suppressNextClick() {
  document.addEventListener('click', suppressClick, { capture: true });

  const timeout = window.setTimeout(stopSuppressingClicks, CHECKBOX_CLICK_SUPPRESS_MS);

  function suppressClick(event: Event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    stopSuppressingClicks();
  }

  function stopSuppressingClicks() {
    document.removeEventListener('click', suppressClick, { capture: true });
    window.clearTimeout(timeout);
  }
}

interface SignatureB64State {
  initials: { initialsB64: string; initialsImgId: UUID } | null;
  signature: {
    signatureB64: string;
    signatureImgId: UUID;
  } | null;
}

function useSetSignatureB64() {
  const client = useApolloClient();
  const { signatureImg, signatureInitialsImg } = useGetUserSignatureImg();

  useAsync(async () => {
    if (signatureImg?.filestackUrl) {
      try {
        const signatureB64 = await fetchSignatureImageB64Url(client, {
          fileId: signatureImg.id,
          url: signatureImg.filestackUrl,
        });
        setSignatureB64({ signature: { signatureB64, signatureImgId: signatureImg.id } });
      } catch (error) {
        console.error(error);
        setSignatureB64({ signature: null });
        dispatchSignatureImageLoadError();
      }
    }

    if (signatureInitialsImg?.filestackUrl) {
      try {
        const initialsB64 = await fetchSignatureImageB64Url(client, {
          fileId: signatureInitialsImg.id,
          url: signatureInitialsImg.filestackUrl,
        });
        setSignatureB64({ initials: { initialsB64, initialsImgId: signatureInitialsImg.id } });
      } catch (error) {
        console.error(error);
        setSignatureB64({ initials: null });
        dispatchSignatureImageLoadError();
      }
    }
  }, [client, signatureImg?.filestackUrl, signatureInitialsImg?.filestackUrl]);
}

/**
 * Signature stamps embed this base64 payload into XFDF, and the server rejects stamps whose
 * payload isn't a data:image/ URL. Validating and refreshing here prevents an expired-policy text
 * response from ever becoming stamp image data.
 */
async function fetchSignatureImageB64Url(
  client: ReturnType<typeof useApolloClient>,
  options: { fileId: UUID; url: string }
) {
  const { fileId, url } = options;
  const blob = await fetchFreshFilestackBlob({
    expectedMimePrefix: 'image/',
    refreshUrl: createDocFileUrlRefresher(client, fileId),
    sentryContext: { fileId, source: 'signature-image-b64' },
    url,
  });
  const { b64Url } = await blobToBase64(blob);

  return b64Url;
}

function dispatchSignatureImageLoadError() {
  pushNotification({
    key: uuid4(),
    message: 'Could not load your saved signature image. Please refresh and try again.',
    variant: 'error',
  });
}

export function comparePDFAnnotationPosition(
  annotA: Core.Annotations.Annotation,
  annotB: Core.Annotations.Annotation
): number {
  const pageDiff = annotA.getPageNumber() - annotB.getPageNumber();
  const annotationsOnSamePage = pageDiff === 0;
  if (annotationsOnSamePage) {
    const yDiff = annotA.Y - annotB.Y;
    const annotationsSameY = yDiff === 0;
    if (annotationsSameY) return annotA.X - annotB.X;

    return yDiff;
  }

  return pageDiff;
}

export function getIsPDFFieldComplete(field: Core.Annotations.Forms.Field): boolean {
  const fieldType = field.getFieldType();
  if (fieldType === PDFFieldType.SigFormField)
    return Boolean(
      field.widgets?.every(widget => {
        const isSigWidget = widget instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation;
        return isSigWidget ? Boolean(widget.getAssociatedSignatureAnnotation()) : false;
      })
    );

  return Boolean(field.value);
}

interface DragPoints {
  eventPageNum: number;
  offsetLeftPx: number;
  offsetTopPx: number;
  pageCoords: { x: number; y: number };
}

function getDragPoints(args: {
  documentViewer: Core.DocumentViewer;
  event: MouseEvent;
  state: DragState;
  coords?: PDFPageCoordsInfo;
}): DragPoints | null {
  const { documentViewer, event, state } = args;
  const coords = args.coords ?? getPDFPageCoords(documentViewer, { event });
  if (!coords) return null;

  const {
    eventPageNum,
    pageCoords,
    windowCoords: { x, y },
  } = coords;
  const pageWidgetContainer = getPageWidgetContainer(documentViewer, eventPageNum);
  if (!state.draggedEl?.el.parentElement || !pageWidgetContainer)
    return {
      eventPageNum,
      offsetLeftPx: state.mouseDownX,
      offsetTopPx: state.mouseDownY,
      pageCoords,
    };

  const rawDeltaY = y - state.mouseDownY;
  const rawDeltaX = x - state.mouseDownX;
  const rotation = documentViewer.getCompleteRotation(eventPageNum);
  const { dx: deltaX, dy: deltaY } = rotateDelta(rawDeltaX, rawDeltaY, rotation);
  const zoom = documentViewer.getZoomLevel();
  const isRotated90or270 =
    rotation === pdfTronCore.PageRotation.E_90 || rotation === pdfTronCore.PageRotation.E_270;

  if (isRotated90or270) {
    const fieldVisualHeightPx = state.draggedEl.annotation.getHeight() * zoom;
    const maxTopPx = Math.max(0, pageWidgetContainer.clientHeight - fieldVisualHeightPx);
    const safeTopPx = Math.min(maxTopPx, Math.max(0, state.offsetTop + deltaY));
    return {
      eventPageNum,
      offsetLeftPx: state.offsetLeft + deltaX,
      offsetTopPx: safeTopPx,
      pageCoords,
    };
  }

  const fieldVisualWidthPx = state.draggedEl.annotation.getWidth() * zoom;
  const fieldVisualHeightPx = state.draggedEl.annotation.getHeight() * zoom;
  const maxLeftPx = pageWidgetContainer.clientWidth - fieldVisualWidthPx;
  const maxTopPx = pageWidgetContainer.clientHeight - fieldVisualHeightPx;
  const safeLeftPx = Math.min(maxLeftPx, Math.max(0, state.offsetLeft + deltaX));
  const safeTopPx = Math.min(maxTopPx, Math.max(0, state.offsetTop + deltaY));
  return {
    eventPageNum,
    offsetLeftPx: safeLeftPx,
    offsetTopPx: safeTopPx,
    pageCoords,
  };
}

function getDragHandleDeltas(
  documentViewer: Core.DocumentViewer,
  options: {
    dragHandleState: DragHandleState;
    event: MouseEvent;
    eventType: 'mousemove' | 'mouseup';
  }
): {
  deltaX: number;
  deltaY: number;
} {
  const { dragHandleState, event, eventType } = options;
  if (!dragHandleState) return { deltaX: 0, deltaY: 0 };

  const zoom = documentViewer.getZoomLevel();
  const isMouseMove = eventType === 'mousemove';
  const rawDeltaY = event.pageY - dragHandleState.y;
  const deltaY = isMouseMove
    ? rawDeltaY
    : convertDimensionUsingZoom(rawDeltaY, {
        direction: ZoomConvertDir.From,
        zoom,
      });
  const rawDeltaX = event.pageX - dragHandleState.x;
  const deltaX = isMouseMove
    ? rawDeltaX
    : convertDimensionUsingZoom(rawDeltaX, {
        direction: ZoomConvertDir.From,
        zoom,
      });
  return { deltaX, deltaY };
}

export function convertDimensionUsingZoom(
  px: number,
  options: { direction: ZoomConvertDir; zoom: number }
): number {
  const { direction, zoom } = options;
  return direction === ZoomConvertDir.To ? px * zoom : px / zoom;
}

function getMaxFieldExtentPdf(
  documentViewer: Core.DocumentViewer,
  annotation: Core.Annotations.WidgetAnnotation
): { maxHeightPdf: number; maxWidthPdf: number } {
  const pageNumber = annotation.getPageNumber();
  return {
    maxHeightPdf: documentViewer.getPageHeight(pageNumber) - annotation.Y,
    maxWidthPdf: documentViewer.getPageWidth(pageNumber) - annotation.X,
  };
}

function getMaxFieldScale(
  documentViewer: Core.DocumentViewer,
  annotation: Core.Annotations.WidgetAnnotation
): number {
  const { maxHeightPdf, maxWidthPdf } = getMaxFieldExtentPdf(documentViewer, annotation);
  const isQuarterRotated = [90, 270].includes(
    getPageRotationDeg(documentViewer, annotation.getPageNumber())
  );
  const fieldExtentX = isQuarterRotated ? annotation.Height : annotation.Width;
  const fieldExtentY = isQuarterRotated ? annotation.Width : annotation.Height;
  return Math.min(maxWidthPdf / fieldExtentX, maxHeightPdf / fieldExtentY);
}

// Largest field width before hitting a page edge. On 90/270 pages the width grows along the
// page's vertical axis, so it's bounded by the room below the field, not to its right.
function getMaxFieldWidthPdf(
  documentViewer: Core.DocumentViewer,
  annotation: Core.Annotations.WidgetAnnotation
): number {
  const { maxHeightPdf, maxWidthPdf } = getMaxFieldExtentPdf(documentViewer, annotation);
  const isQuarterRotated = [90, 270].includes(
    getPageRotationDeg(documentViewer, annotation.getPageNumber())
  );
  return isQuarterRotated ? maxHeightPdf : maxWidthPdf;
}

/**
 * After a resize, keep the field's visual top-left fixed. PDFTron pins the upright field at the
 * rotated PDF (X, Y), so a different corner anchors per rotation and changing W/H shifts the
 * field. On 90/270 the axes swap, so a size change is countered on the opposite PDF axis
 * (verified at 0°/270°; 90°/180° derived from the same model).
 */
function anchorResizedFieldTopLeft(
  documentViewer: Core.DocumentViewer,
  annotation: Core.Annotations.WidgetAnnotation,
  oldWidth: number,
  oldHeight: number
): void {
  const rotationDeg = getPageRotationDeg(documentViewer, annotation.getPageNumber());
  const widthDelta = oldWidth - annotation.Width;
  const heightDelta = oldHeight - annotation.Height;
  if (rotationDeg === 90) annotation.setY(annotation.Y + widthDelta);
  if (rotationDeg === 180) {
    annotation.setX(annotation.X + widthDelta);
    annotation.setY(annotation.Y + heightDelta);
  }
  if (rotationDeg === 270) annotation.setX(annotation.X + heightDelta);
}

function getMultilineContentHeightScreenPx(
  widget: Core.Annotations.WidgetAnnotation
): number | null {
  const textEl = widget.innerElement;
  if (!(textEl instanceof HTMLTextAreaElement)) return null;

  const value = String(widget.getField().getValue());
  textEl.value = value === '' ? 'X' : value;
  const prevHeightStyle = textEl.style.height;
  const prevOverflowStyle = textEl.style.overflow;
  textEl.style.overflow = 'hidden';
  textEl.style.height = '0px';
  const contentHeightPx = textEl.scrollHeight;
  textEl.value = value;
  textEl.style.height = prevHeightStyle;
  textEl.style.overflow = prevOverflowStyle;

  return contentHeightPx + 2 * defaultPDFFieldAttributes.focusedBorderSizePx;
}

// The PDF height a multiline field needs to fit its text. Null when the widget isn't a textarea
// (no content to measure).
function getMultilineFitHeightPx(
  documentViewer: Core.DocumentViewer,
  widget: Core.Annotations.WidgetAnnotation
): number | null {
  const contentHeightScreenPx = getMultilineContentHeightScreenPx(widget);
  if (contentHeightScreenPx == null) return null;

  return Math.ceil(
    convertDimensionUsingZoom(contentHeightScreenPx, {
      direction: ZoomConvertDir.From,
      zoom: documentViewer.getZoomLevel(),
    })
  );
}

export function kebabCaseCSS(cssProps: React.CSSProperties): Record<string, string> {
  return Object.entries(cssProps).reduce(
    (acc, [camelCaseCSSKey, cssVal]) => ({ ...acc, [kebabCase(camelCaseCSSKey)]: cssVal }),
    {}
  );
}

export function rotateDelta(dx: number, dy: number, rotation: number): { dx: number; dy: number } {
  switch (rotation) {
    case pdfTronCore.PageRotation.E_90:
      return { dx: dy, dy: -dx };
    case pdfTronCore.PageRotation.E_180:
      return { dx: -dx, dy: -dy };
    case pdfTronCore.PageRotation.E_270:
      return { dx: -dy, dy: dx };
    case pdfTronCore.PageRotation.E_0:
    default:
      return { dx, dy };
  }
}

export function getPageRotationDeg(documentViewer: Core.DocumentViewer, pageNum: number): number {
  const rotation = documentViewer.getCompleteRotation(pageNum);
  switch (rotation) {
    case pdfTronCore.PageRotation.E_90:
      return 90;
    case pdfTronCore.PageRotation.E_180:
      return 180;
    case pdfTronCore.PageRotation.E_270:
      return 270;
    case pdfTronCore.PageRotation.E_0:
    default:
      return 0;
  }
}

function getCheckboxGroupPrimary(
  group: Core.Annotations.Annotation[]
): Core.Annotations.CheckButtonWidgetAnnotation | undefined {
  return group.find(
    (member): member is Core.Annotations.CheckButtonWidgetAnnotation =>
      member instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation
  );
}

function checkboxLabelHitId(labelId: string | number): string {
  return `aline-checkbox-label-hit-${labelId}`;
}

function isCheckboxOptionLabel(
  annotation: Core.Annotations.Annotation
): annotation is Core.Annotations.FreeTextAnnotation {
  return (
    annotation instanceof pdfTronCore.Annotations.FreeTextAnnotation &&
    !!annotation.getCustomData(PDFCustomDataKey.checkboxOptionLabel)
  );
}

function findCheckboxOptionLabelMember(
  members: GroupMemberStartPosition[] | undefined
): GroupMemberStartPosition | undefined {
  return members?.find(member => isCheckboxOptionLabel(member.annotation));
}

function canDragPdfFields(tabId: UUID | null): boolean {
  const userState = getEditorUserStateForTabId(tabId);
  const walkThrough = new URLSearchParams(window.location.search).has(walkThroughParamKey);
  return !walkThrough && !userState?.isInSigningCeremony && !userState?.isSigned;
}

/** Signing mode allows checkbox toggle (click) but not drag repositioning. */
function canInteractWithPdfCheckbox(tabId: UUID | null): boolean {
  const userState = getEditorUserStateForTabId(tabId);
  if (userState?.isSigned) return false;
  if (userState?.isInSigningCeremony) return true;
  return canDragPdfFields(tabId);
}

function getCheckboxOptionLabelWidthPts(
  labelAnnotation: Core.Annotations.FreeTextAnnotation
): number {
  const maxWidthPt = labelAnnotation.Width;
  const text = (labelAnnotation.getContents() ?? '').trim();
  if (!text) return 0;

  const ctx = createAlineCanvas().getContext('2d');
  if (!ctx) return maxWidthPt;

  ctx.font = `${defaultPDFFieldAttributes.fontSizePx}pt ${alineDefaultFontFamily}`;
  return Math.min(maxWidthPt, Math.ceil(ctx.measureText(text).width));
}

function isCheckboxGroupClick(
  windowCoords: { x: number; y: number },
  mouseDownX: number,
  mouseDownY: number
): boolean {
  return (
    Math.hypot(windowCoords.x - mouseDownX, windowCoords.y - mouseDownY) <=
    CHECKBOX_DRAG_CLICK_THRESHOLD_PX
  );
}

function isCheckboxGroupDrag(state: DragState): boolean {
  return state.draggedEl?.annotation instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation;
}

function notifyCheckboxFieldDisabled(disabledReason: string): void {
  pushNotification({
    key: uuid4(),
    message: disabledReason,
    variant: 'warning',
  });
}

async function lockSquareCheckboxWidgetsAndRedraw(
  annotationManager: Core.AnnotationManager,
  field: Core.Annotations.Forms.Field
): Promise<boolean> {
  const alineWidgets = (field.widgets ?? []).filter(widget =>
    getIsAlineFieldName(widget.fieldName)
  );
  if (!alineWidgets.length) return false;

  const sidePx = defaultPDFFieldAttributes.fontSizePx;
  for (const widget of alineWidgets) {
    widget.Width = sidePx;
    widget.Height = sidePx;
  }
  const pageNum = alineWidgets[0]?.PageNumber;
  if (pageNum !== undefined) {
    await annotationManager.drawAnnotations({ majorRedraw: true, pageNumber: pageNum });
  }

  return true;
}

async function activateCheckboxOptionGroup(
  documentViewer: Core.DocumentViewer,
  primary: Core.Annotations.CheckButtonWidgetAnnotation
): Promise<void> {
  const callbacks = checkboxPdfFieldCallbacksByViewer.get(documentViewer);
  const disabledReason = await callbacks?.getDisabledReason(primary.fieldName);
  if (disabledReason) {
    notifyCheckboxFieldDisabled(disabledReason);
    return;
  }

  const field = primary.getField();
  if (!field) return;

  const value = String(field.getValue() ?? '');
  const nextValue = value === CHECKBOX_CHECKED ? PDF_CHECKBOX_UNCHECKED : CHECKBOX_CHECKED;
  field.setValue(nextValue);

  await documentViewer.getAnnotationManager().drawAnnotations({
    majorRedraw: true,
    pageNumber: primary.PageNumber,
  });
  await callbacks?.sync(field);
}

function clampCheckboxGroupPrimaryPagePosition(args: {
  desiredPrimaryX: number;
  desiredPrimaryY: number;
  fieldExtentX: number;
  fieldExtentY: number;
  pageHeight: number;
  pageWidth: number;
  primaryStart: { startX: number; startY: number };
  labelStart?: GroupMemberStartPosition;
}): { x: number; y: number } {
  const {
    desiredPrimaryX,
    desiredPrimaryY,
    fieldExtentX,
    fieldExtentY,
    labelStart,
    pageHeight,
    pageWidth,
    primaryStart,
  } = args;

  const labelAnnotation =
    labelStart && isCheckboxOptionLabel(labelStart.annotation) ? labelStart.annotation : null;

  let groupWidth = fieldExtentX;
  if (labelAnnotation && labelStart) {
    groupWidth =
      labelStart.startX - primaryStart.startX + getCheckboxOptionLabelWidthPts(labelAnnotation);
  }

  const safeX = Math.min(Math.max(0, pageWidth - groupWidth), Math.max(0, desiredPrimaryX));
  let safeY = Math.min(pageHeight - fieldExtentY, Math.max(0, desiredPrimaryY));

  if (labelAnnotation && labelStart) {
    const offsetY = labelStart.startY - primaryStart.startY;
    const groupBottom = safeY + offsetY + labelAnnotation.getHeight();
    if (groupBottom > pageHeight) {
      safeY = Math.max(0, pageHeight - offsetY - labelAnnotation.getHeight());
    }
  }

  return { x: safeX, y: safeY };
}

/**
 * Returns null if the document isn't loaded yet, the page hasn't been
 * rendered, or no matching widget exists.
 */
export function findPDFSignatureWidgetByFieldId(
  documentViewer: Core.DocumentViewer,
  fieldNodeId: string
): Core.Annotations.SignatureWidgetAnnotation | null {
  const annotations = documentViewer.getAnnotationManager().getAnnotationsList();
  return (
    annotations.find(
      (annot): annot is Core.Annotations.SignatureWidgetAnnotation =>
        annot instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation &&
        annot.fieldName === fieldNodeId
    ) ?? null
  );
}

/**
 * Single source of truth for stamping a signature, reached from both the
 * signature-widget click handler and the mobile signing sheet's Sign Here.
 */
export async function applyPDFSignatureStamp(options: {
  documentViewer: Core.DocumentViewer;
  signatureB64: string;
  signatureImgId: string;
  widget: Core.Annotations.SignatureWidgetAnnotation;
}): Promise<void> {
  const { documentViewer, signatureB64, signatureImgId, widget } = options;
  const annotationManager = documentViewer.getAnnotationManager();
  const rotation = getPageRotationDeg(documentViewer, widget.PageNumber);
  const needsSwap = rotation === 90 || rotation === 270;
  const stamp = new pdfTronCore.Annotations.StampAnnotation({
    Height: needsSwap ? widget.Width : widget.Height,
    PageNumber: widget.PageNumber,
    Rotation: rotation,
    Width: needsSwap ? widget.Height : widget.Width,
    X: widget.X,
    Y: widget.Y,
  });
  stamp.setCustomData(PDFCustomDataKey.nodeId, widget.fieldName);
  stamp.setCustomData(PDFCustomDataKey.signatureImgId, signatureImgId);
  await setSignatureStampImageData(stamp, signatureB64);
  annotationManager.addAnnotation(stamp);
  widget.setAssociatedSignatureAnnotation(stamp);
  await annotationManager.drawAnnotations({
    majorRedraw: true,
    pageNumber: widget.PageNumber,
  });
}

export function clearPDFSignatureStamp(
  documentViewer: Core.DocumentViewer,
  widget: Core.Annotations.SignatureWidgetAnnotation
): void {
  widget.clearSignature(documentViewer.getAnnotationManager());
}

function setSignatureWidgetVisualState(parentEl: HTMLElement, fieldWidthPx: number): void {
  const signatureFieldElement = parentEl.querySelector<HTMLElement>(
    `[${SIGNATURE_FIELD_DATA_ATTR}]`
  );
  if (!signatureFieldElement) return;
  const scale = fieldWidthPx / defaultPDFFieldAttributes.sigFieldWidthPx;
  signatureFieldElement.style.position = 'absolute';
  signatureFieldElement.style.left = '0';
  signatureFieldElement.style.top = '0';
  signatureFieldElement.style.transformOrigin = 'top left';
  signatureFieldElement.style.transform = `scale(${scale})`;
}

function stopFieldTapFromTriggeringLink(el: HTMLElement): void {
  if (el.dataset.alineTapStopAttached) return;
  el.dataset.alineTapStopAttached = 'true';
  el.addEventListener('tap', event => event.stopPropagation());
}
